import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { LiveDot, Pct, fmtPrice, fmtBig, CoinIcon } from "@/components/layernav/primitives";
import { HOME_CONTAINER } from "./shared";

const LI_COLOR = (v: number) =>
  v < 20 ? "#DC2626" : v < 40 ? "#F97316" : v < 60 ? "#A3A3A3" : v < 80 ? "#84CC16" : "#22C55E";

export const PulseBar = () => {
  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    refetchInterval: 90000,
  });
  const { data: global } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
  });
  const { data: li } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const coins = markets?.coins || [];
  if (!coins.length && !global?.available && !li) return null;

  return (
    <div data-testid="newslayer-pulse" className="border-b border-[#1c212e] bg-[#07080c]">
      <div className={`${HOME_CONTAINER} flex items-center h-10 gap-4 sm:gap-5 overflow-x-auto scrollbar-none`}>
        <span className="flex items-center gap-2 shrink-0 pr-3 sm:pr-4 border-r border-[#1c212e]">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#7a8299]">
            NewsLayer Pulse
          </span>
          <svg viewBox="0 0 36 12" className="w-9 h-3 hidden sm:block" aria-hidden>
            <polyline points="0,9 6,6 12,8 18,3 24,5 30,2 36,4" fill="none"
              stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.9" />
          </svg>
          <LiveDot />
          <span className="font-mono text-[9px] font-bold tracking-widest text-[#0dd480]">LIVE</span>
        </span>
        {coins.map((c: any) => (
          <Link key={c.symbol} to={`/coin/${c.symbol}`} data-testid={`pulse-${c.symbol}`}
            className={`items-center gap-1.5 shrink-0 hover:opacity-75 transition-opacity ${
              ["DOGE", "ADA"].includes(c.symbol) ? "flex xl:hidden" : "flex"}`}>
            <CoinIcon symbol={c.symbol} size={16} />
            <span className="font-mono text-[11px] font-bold text-[#7a8299]">{c.symbol}</span>
            <span className="font-mono text-[11px] text-[#f2f5fc]">{fmtPrice(c.price)}</span>
            <Pct v={c.change_24h} className="text-[10px]" />
          </Link>
        ))}
        <span className="ml-auto flex items-center gap-4 sm:gap-5 shrink-0 pl-2">
          {global?.available && (
            <>
              <span className="flex items-center gap-1.5 shrink-0" data-testid="pulse-mcap">
                <span className="font-mono text-[9px] uppercase tracking-wider text-[#7a8299]">Total Cap</span>
                <span className="font-mono text-[11px] font-semibold text-[#f2f5fc]">{fmtBig(global.total_market_cap_usd)}</span>
                <Pct v={global.market_cap_change_24h_pct} className="text-[9px]" />
              </span>
              <span className="hidden md:flex items-center gap-1.5 shrink-0" data-testid="pulse-volume">
                <span className="font-mono text-[9px] uppercase tracking-wider text-[#7a8299]">24H Vol</span>
                <span className="font-mono text-[11px] font-semibold text-[#f2f5fc]">{fmtBig(global.volume_24h_usd)}</span>
              </span>
            </>
          )}
          {li && (
            <span className="flex items-center gap-1.5 shrink-0" data-testid="pulse-layer-index">
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#7a8299]">Layer Index</span>
              <span className="font-mono text-[11px] font-bold" style={{ color: LI_COLOR(li.value) }}>
                {li.value} {li.label}
              </span>
            </span>
          )}
        </span>
      </div>
    </div>
  );
};
